import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  Badge,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import AssessmentIcon from "@mui/icons-material/Assessment";

// Services
import { getCustomerById } from "../../services/Customer";
import { getSaleBillData } from "../../services/Sales";

// Components
import CustomerDataView from "../../components/CustomerDataView";
import BillListTable from "../../components/BillListTable";

// Styled Components
const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const billStatus = [
  { value: "DRAFT", label: "Draft" },
  { value: "REJECTED", label: "Rejected" },
  { value: "APPROVED", label: "Approved" },
  { value: "AWAITING", label: "Awaiting Approval" },
  { value: "PENDING", label: "Pending" },
  { value: "SETTLED", label: "Settled" },
];

function CustomerView() {
  const theme = useTheme();
  const history = useHistory();
  const { customerId } = useParams();

  const [customer, setCustomer] = useState(null);
  const [customerError, setCustomerError] = useState("");
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter state
  const [filter, setFilter] = useState({
    _billNumber: "",
    _products: [],
    _barcode: "",
    _addedBy: "",
    _customer: [customerId],
    _status: "",
  });

  useEffect(() => {
    fetchCustomerData();
  }, [customerId]);

  const fetchCustomerData = async () => {
    setIsLoading(true);
    try {
      const result = await getCustomerById(customerId);

      if (result !== "Something went wrong...") {
        setCustomer(result);
        const initialFilter = {
          _billNumber: "",
          _products: [],
          _barcode: "",
          _addedBy: "",
          _customer: [customerId],
          _status: "",
        };
        setFilter(initialFilter);
        await fetchSaleBillData(initialFilter, 0);
      } else {
        setCustomerError("Unable to fetch customer data!");
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      setCustomerError("Unable to fetch customer data!");
    } finally {
      setIsLoading(false);
    }
  };

  const resetFilters = () => {
    const resetFilter = {
      _billNumber: "",
      _products: [],
      _barcode: "",
      _addedBy: "",
      _customer: [customerId],
      _status: "",
    };
    setFilter(resetFilter);
    setPage(0);
    fetchSaleBillData(resetFilter, 0);
  };

  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  const fetchSaleBillData = async (productFilter, pageNo) => {
    setIsTableLoading(true);
    try {
      const result = await getSaleBillData(pageNo, rowsPerPage, productFilter);
      const saleBillList = result.content;

      setTotalPages(result.totalPages);
      setTotalRows(result.totalElements);

      const newSet = [];
      if (saleBillList) {
        for (let x = 0; x < saleBillList.length; x++) {
          newSet.push(
            createData(
              saleBillList[x].sellingDate,
              saleBillList[x].billNumber,
              saleBillList[x].customer?.customerName,
              saleBillList[x].status,
              saleBillList[x].totalProfit,
              saleBillList[x].totalPrice,
              saleBillList[x].billId
            )
          );
        }
      }
      setRows(newSet);
    } catch (error) {
      console.error("Error fetching sale bills:", error);
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchSaleBillData(filter, value - 1);
  };

  const columns = [
    {
      id: "date",
      label: "Date",
      minWidth: 100,
    },
    {
      id: "billNumber",
      label: "Bill Number",
      minWidth: 120,
      format: (value) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ReceiptIcon sx={{ fontSize: 16, mr: 1, color: "primary.main" }} />
          {value}
        </Box>
      ),
    },
    {
      id: "customer",
      label: "Customer",
      minWidth: 150,
      format: (value) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PersonIcon sx={{ fontSize: 16, mr: 1, color: "primary.main" }} />
          {value}
        </Box>
      ),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 130,
      format: (value) => {
        let color = "default";
        switch (value) {
          case "APPROVED":
            color = "success";
            break;
          case "REJECTED":
            color = "error";
            break;
          case "AWAITING":
            color = "warning";
            break;
          case "PENDING":
            color = "info";
            break;
          case "SETTLED":
            color = "primary";
            break;
          default:
            color = "default";
        }
        return (
          <Chip
            label={value}
            color={color}
            size="small"
            variant={value === "APPROVED" ? "filled" : "outlined"}
          />
        );
      },
    },
    {
      id: "totalProfit",
      label: "Total Profit",
      minWidth: 120,
      format: (value) => (
        <Typography variant="body2" fontWeight="600" color="success.main">
          ${value.toLocaleString("en-US")}
        </Typography>
      ),
    },
    {
      id: "totalAmount",
      label: "Total Amount",
      minWidth: 120,
      format: (value) => (
        <Typography variant="body2" fontWeight="600">
          ${value.toLocaleString("en-US")}
        </Typography>
      ),
    },
    { id: "action", label: "Actions", minWidth: 150 },
  ];

  function createData(
    date,
    billNumber,
    customer,
    status,
    totalProfit,
    totalAmount,
    action
  ) {
    return {
      date,
      billNumber,
      customer,
      status,
      totalProfit,
      totalAmount,
      action,
    };
  }

  const handleAction = async (event, id) => {
    switch (event) {
      case "view":
        // Navigate to bill view
        history.push(`/bill/view/${id}`);
        break;
      case "production":
        // Add payment logic
        break;
      case "disable":
        // Delete bill logic
        break;
      default:
        return;
    }
  };

  const calculateStats = () => {
    const totalBills = rows.length;
    const approvedBills = rows.filter(
      (row) => row.status === "APPROVED"
    ).length;
    const totalRevenue = rows.reduce((sum, row) => sum + row.totalAmount, 0);
    const totalProfit = rows.reduce((sum, row) => sum + row.totalProfit, 0);

    return { totalBills, approvedBills, totalRevenue, totalProfit };
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading customer details...
        </Typography>
      </Box>
    );
  }

  if (customerError) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Typography variant="h6" color="error">
          {customerError}
        </Typography>
      </Paper>
    );
  }

  const stats = calculateStats();

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        m: { xs: 1, md: 3 },
        borderRadius: 2,
        bgcolor: "#f9fafb",
      }}
    >
      {/* Header with close button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AccountCircleIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Customer Details
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Customer Information */}
      {customer && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Customer Basic Info */}
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="primary.main"
                  >
                    Customer Information
                  </Typography>
                </Box>

                <CustomerDataView customer={customer} />
              </CardContent>
            </Card>
          </Grid>

          {/* Customer Statistics */}
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <AssessmentIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="primary.main"
                  >
                    Customer Statistics
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Total Bills
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="600"
                        color="primary.main"
                      >
                        {totalRows}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Approved Bills
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="600"
                        color="success.main"
                      >
                        {stats.approvedBills}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Total Revenue
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="600"
                        color="info.main"
                      >
                        ${stats.totalRevenue.toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Total Profit
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="600"
                        color="warning.main"
                      >
                        ${stats.totalProfit.toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Bills Section */}
      <Card elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <ReceiptIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Sale Bills
            </Typography>
            <Badge badgeContent={totalRows} color="primary" sx={{ ml: 1 }} />
          </Box>

          {/* Filters */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  name="_status"
                  value={filter._status}
                  onChange={(event) =>
                    handleFilterChange(event.target.name, event.target.value)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {billStatus.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={8}>
                <Box display="flex" justifyContent="flex-end">
                  <Stack direction="row" spacing={2}>
                    <Tooltip title="Reset Filters">
                      <ActionButton
                        variant="outlined"
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={resetFilters}
                      >
                        Reset
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="Apply Filters">
                      <ActionButton
                        variant="contained"
                        color="primary"
                        startIcon={<FilterListIcon />}
                        onClick={() => {
                          setPage(0);
                          fetchSaleBillData(filter, 0);
                        }}
                      >
                        Apply Filters
                      </ActionButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Table Content */}
          {isTableLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box
              sx={{
                p: 5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.grey[500], 0.05),
                borderRadius: 1,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No sale bills found. Try adjusting your filters.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <BillListTable
                columns={columns}
                rows={rows}
                page={page}
                setPage={setPage}
                tablePagin={false}
                totalPages={totalPages}
                handleAction={handleAction}
                showActions={{
                  view: "Bill view",
                  production: "Add payment",
                  disable: "Delete bill",
                }}
                actionRoles={{
                  view: ["SALE_BILL_VIEW", "ROLE_ADMIN"],
                  production: ["SALE_BILL_PAYMENT_UPDATE", "ROLE_ADMIN"],
                  disable: ["SALE_BILL_DELETE", "ROLE_ADMIN"],
                }}
                tableProps={{
                  sx: {
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: alpha("#000", 0.04),
                    },
                  },
                }}
              />
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <PaginationContainer>
              <Pagination
                count={totalPages}
                page={page + 1}
                variant="outlined"
                shape="rounded"
                color="primary"
                onChange={handleChange}
                size="medium"
              />
            </PaginationContainer>
          )}
        </CardContent>
      </Card>
    </Paper>
  );
}

export default CustomerView;
